import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TextField,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
  Speed,
  Share,
  Settings as SettingsIcon,
  PersonAdd,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  AutoAwesome as SprintIcon,
  AccessTime,
  BoltOutlined,
  TrendingUp,
  AccountBalance as BudgetIcon,
  Tune as FilterIcon
} from '@mui/icons-material';
import TaskList from '../components/TaskList';
import KanbanBoard from '../components/KanbanBoard';
import GanttChart from '../components/GanttChartDragDrop';
import TaskForm from '../components/TaskForm';
import ProjectChat from '../components/ProjectChat';
import AttachmentList from '../components/AttachmentList';
import ProjectAIAnalysis from '../components/ProjectAIAnalysis';
import ShareProjectDialog from '../components/ShareProjectDialog';
import MilestoneForm from '../components/MilestoneForm';
import SprintBoard from '../components/SprintBoard';
import TimeTracker from '../components/TimeTracker';
import AutomationBuilder from '../components/AutomationBuilder';
import WorkloadView from '../components/WorkloadView';
import BudgetView from '../components/BudgetView';
import { projectService, taskService, exportService, milestoneService, budgetService, customFieldService } from '../services/api';
import CustomFieldsManager from '../components/CustomFieldsManager';
import { TEAM_ROLES, PROJECT_DOMAINS, ACCESS_LEVELS } from '../constants/projectOptions';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          Une erreur est survenue dans cet onglet : {this.state.error?.message || 'Erreur inconnue'}
        </Alert>
      );
    }
    return this.props.children;
  }
}

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useOutletContext();
  
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [settingsForm, setSettingsForm] = useState({
    domain: '',
    title: '',
    description: '',
    status: '',
    priority: ''
  });
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Onglets
  const [currentTab, setCurrentTab] = useState(0);

  // Menu projet
  const [anchorEl, setAnchorEl] = useState(null);

  // Formulaire tâche
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [isEditTask, setIsEditTask] = useState(false);

  // Dialogue de confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Share Dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogTab, setShareDialogTab] = useState(0);

  // Background Customization - removed as it is now in global header/layout or can be re-added differently if specific to project
  // Keeping logic if needed but removing UI from local AppBar
  
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Milestones
  const [milestones, setMilestones] = useState([]);
  const [milestoneFormOpen, setMilestoneFormOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);

  // Custom fields
  const [customFields, setCustomFields] = useState([]);
  const [customFieldsManagerOpen, setCustomFieldsManagerOpen] = useState(false);
  const [customFieldFilters, setCustomFieldFilters] = useState({});
  const [showCustomFilters, setShowCustomFilters] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchTasks();
    fetchPhases();
    fetchMilestones();
    fetchCustomFields();
  }, [id]);

  useEffect(() => {
    const action = searchParams.get('action');
    const tab = searchParams.get('tab');
    
    if (action === 'requests') {
      setShareDialogTab(1);
      setShareDialogOpen(true);
    }
    
    if (tab === 'discussion') {
      setCurrentTab(10);
    } else if (tab === 'settings') {
      setCurrentTab(12);
    }
  }, [searchParams]);

  const fetchProject = async () => {
    try {
      const response = await projectService.getById(id);
      setProject(response.data.project);
      setSettingsForm({
        domain: response.data.project.domain || '',
        title: response.data.project.title || '',
        description: response.data.project.description || '',
        status: response.data.project.status || 'active',
        priority: response.data.project.priority || 'medium'
      });
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement du projet');
      console.error(err);
    }
  };

  const fetchTasks = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await taskService.getByProject(id);
      setTasks(response.data.tasks);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des tâches');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchPhases = async () => {
    try {
      const response = await taskService.getProjectPhases(id);
      setPhases(response.data.phases);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await milestoneService.getByProject(id);
      setMilestones(response.data.milestones || []);
    } catch (err) {
      console.error('fetchMilestones error:', err);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await customFieldService.getProjectFields(id);
      setCustomFields(response.data.fields || []);
    } catch (err) {
      // Table may not exist yet on first run — silently ignore
      console.debug('fetchCustomFields:', err.message);
    }
  };

  const handleUpdateProjectSettings = async () => {
    try {
      await projectService.update(id, settingsForm);
      setSuccess('Projet mis à jour avec succès');
      fetchProject();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur lors de la mise à jour du projet');
    }
  };

  const handleDeleteProject = async () => {
    try {
      await projectService.delete(id);
      navigate('/projects');
    } catch (err) {
      setError('Erreur lors de la suppression du projet');
      setDeleteProjectDialogOpen(false);
    }
  };

  const handleUpdateMember = async (memberId, role, functionalRole) => {
    try {
      await projectService.updateMember(id, memberId, { role, functionalRole });
      setSuccess('Membre mis à jour');
      setEditingMember(null);
      fetchProject();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Erreur lors de la mise à jour du membre');
    }
  };

  const handleAnalyzeProject = async () => {
    try {
      setAnalyzing(true);
      const response = await projectService.analyze(id);
      setAiAnalysis(response.data);
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Impossible de récupérer l\'analyse IA');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportPDF = async () => {
    try {
      handleMenuClose();
      const response = await exportService.exportProjectPDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_${project.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Rapport PDF généré avec succès');
    } catch (err) {
      setError('Erreur lors de la génération du rapport PDF');
    }
  };

  const handleExportERAProject = async () => {
    try {
      handleMenuClose();
      const response = await projectService.exportERAProject(id);
      
      // Créer un lien pour le téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `${project.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.eraproject`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Exportation réussie');
    } catch (err) {
      setError('Erreur lors de l\'exportation du projet');
    }
  };

  const handleCreateTask = () => {
    setIsEditTask(false);
    setEditingTask(null);
    setTaskFormOpen(true);
  };

  const handleEditTask = async (task) => {
    setIsEditTask(true);
    setTaskFormOpen(true);
    try {
      const response = await taskService.getById(task.id);
      setEditingTask(response.data.task);
    } catch (err) {
      setEditingTask(task);
    }
  };

  const handleSubtaskChange = async () => {
    fetchTasks(true);
    if (editingTask?.id) {
      try {
        const response = await taskService.getById(editingTask.id);
        setEditingTask(response.data.task);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTask = async () => {
    try {
      await taskService.delete(taskToDelete.id);
      setSuccess('Tâche supprimée avec succès');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
      fetchProject(); // Rafraîchir pour mettre à jour les statistiques
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression de la tâche');
      setDeleteDialogOpen(false);
    }
  };

  const handleSubmitTask = async (formData) => {
    try {
      const { custom_fields, ...taskData } = formData;
      let taskId;
      if (isEditTask) {
        await taskService.update(editingTask.id, taskData);
        taskId = editingTask.id;
        setSuccess('Tâche modifiée avec succès');
      } else {
        const response = await taskService.create({ ...taskData, project_id: parseInt(id) });
        taskId = response.data.task?.id;
        setSuccess('Tâche créée avec succès');
      }
      // Save custom field values
      if (taskId && custom_fields && Object.keys(custom_fields).length > 0) {
        const values = Object.entries(custom_fields).map(([field_definition_id, value]) => ({
          field_definition_id: parseInt(field_definition_id),
          value: value === null || value === undefined ? '' : String(value)
        }));
        await customFieldService.setTaskValues(taskId, values);
      }
      setTaskFormOpen(false);
      setEditingTask(null);
      fetchTasks(true);
      fetchProject();
      fetchPhases();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement de la tâche');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await taskService.updateStatus(task.id, newStatus);
      setSuccess('Statut mis à jour');
      fetchTasks(true);
      fetchProject();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  if (!project && !loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Projet non trouvé
        </Typography>
        <Button onClick={() => navigate('/projects')} sx={{ mt: 2 }}>
          Retour aux projets
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: '100vh',
        bgcolor: 'transparent',
        backgroundImage: project?.background_url ? `url(${project.background_url})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        transition: 'background-image 0.3s ease-in-out'
      }}
    >
      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* En-tête du projet */}
        {project && (
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 32,
                      bgcolor: project.color || '#3498db',
                      borderRadius: 1,
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, wordBreak: 'break-word' }}>{project.title}</Typography>
                  <Chip label={project.status} color="primary" size="small" />
                  <Chip label={project.priority} color="warning" size="small" variant="outlined" />
                </Box>
                {project.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 3 }, mb: 2 }}>
                    {project.description}
                  </Typography>
                )}
                <Box sx={{ ml: { xs: 0, sm: 3 }, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tâches complétées
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      {project.completed_tasks || 0} / {project.total_tasks || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Membres
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      {project.team_members || 0}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ml: { xs: 0, sm: 3 }, mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Progression globale
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={project.total_tasks > 0 ? (project.completed_tasks / project.total_tasks) * 100 : 0}
                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Box>
              <IconButton onClick={handleMenuOpen} sx={{ flexShrink: 0 }}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => { handleMenuClose(); navigate(`/projects`); }}>
                  Modifier le projet
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); setCustomFieldsManagerOpen(true); }}>
                  Champs personnalisés
                </MenuItem>
                <MenuItem onClick={handleExportPDF}>
                  <PdfIcon sx={{ mr: 1, fontSize: '1.2rem', color: '#ef4444' }} />
                  Rapport PDF
                </MenuItem>
                <MenuItem onClick={handleExportERAProject}>
                  <DownloadIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Exporter (.eraproject)
                </MenuItem>
              </Menu>
            </Box>
          </Paper>
        )}

        {/* Alertes */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Onglets */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Liste" />
            <Tab label="Kanban" />
            <Tab label="Planning" />
            <Tab label="Sprints" icon={<SprintIcon />} iconPosition="start" />
            <Tab label="Équipe" />
            <Tab label="Suivi du temps" icon={<AccessTime />} iconPosition="start" />
            <Tab label="Automatisations" icon={<BoltOutlined />} iconPosition="start" />
            <Tab label="Charge de travail" icon={<TrendingUp />} iconPosition="start" />
            <Tab label="Budget" icon={<BudgetIcon />} iconPosition="start" />
            <Tab label="Fichiers" />
            <Tab label="Discussion" />
            <Tab label="Analyse IA" icon={<Speed />} iconPosition="start" />
            {project?.user_role === 'owner' && <Tab label="Paramètres" icon={<SettingsIcon />} iconPosition="start" />}
          </Tabs>
        </Paper>

        {/* Contenu des onglets */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {currentTab === 0 && (() => {
              // Client-side filter on custom fields
              const filteredTasks = tasks.filter(task => {
                for (const [fieldId, filterVal] of Object.entries(customFieldFilters)) {
                  if (!filterVal) continue;
                  const cfv = (task.custom_field_values || []).find(v => String(v.field_definition_id) === String(fieldId));
                  const taskVal = cfv?.value || '';
                  if (filterVal === '__set__') { if (!taskVal) return false; }
                  else if (!taskVal.toLowerCase().includes(filterVal.toLowerCase())) return false;
                }
                return true;
              });
              return (
                <Box>
                  {customFields.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant={showCustomFilters ? 'contained' : 'outlined'}
                        startIcon={<FilterIcon />}
                        onClick={() => setShowCustomFilters(v => !v)}
                        sx={{ height: 32 }}
                      >
                        Filtres champs
                        {Object.values(customFieldFilters).filter(Boolean).length > 0 && (
                          <Chip label={Object.values(customFieldFilters).filter(Boolean).length} size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Button>
                      {Object.values(customFieldFilters).filter(Boolean).length > 0 && (
                        <Button size="small" color="inherit" onClick={() => setCustomFieldFilters({})}>
                          Effacer
                        </Button>
                      )}
                    </Box>
                  )}
                  {showCustomFilters && customFields.length > 0 && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                      <Grid container spacing={2}>
                        {customFields.map(field => {
                          if (field.field_type === 'checkbox') {
                            return (
                              <Grid item xs={12} sm={6} md={4} key={field.id}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>{field.name}</InputLabel>
                                  <Select
                                    label={field.name}
                                    value={customFieldFilters[field.id] || ''}
                                    onChange={e => setCustomFieldFilters(p => ({ ...p, [field.id]: e.target.value }))}
                                  >
                                    <MenuItem value="">Tous</MenuItem>
                                    <MenuItem value="true">Coché</MenuItem>
                                    <MenuItem value="false">Non coché</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            );
                          }
                          if (field.field_type === 'select') {
                            return (
                              <Grid item xs={12} sm={6} md={4} key={field.id}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>{field.name}</InputLabel>
                                  <Select
                                    label={field.name}
                                    value={customFieldFilters[field.id] || ''}
                                    onChange={e => setCustomFieldFilters(p => ({ ...p, [field.id]: e.target.value }))}
                                  >
                                    <MenuItem value="">Tous</MenuItem>
                                    {(field.options || []).map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                            );
                          }
                          return (
                            <Grid item xs={12} sm={6} md={4} key={field.id}>
                              <TextField
                                size="small"
                                fullWidth
                                label={`${field.name}`}
                                placeholder={`Filtrer par ${field.name.toLowerCase()}...`}
                                value={customFieldFilters[field.id] || ''}
                                onChange={e => setCustomFieldFilters(p => ({ ...p, [field.id]: e.target.value }))}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  )}
                  <TaskList
                    tasks={filteredTasks}
                    onCreateTask={handleCreateTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    onStatusChange={handleStatusChange}
                  />
                </Box>
              );
            })()}
            {currentTab === 1 && (
              <KanbanBoard
                tasks={tasks}
                onCreateTask={handleCreateTask}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onStatusChange={handleStatusChange}
              />
            )}
            {currentTab === 2 && (
              <GanttChart
                tasks={tasks}
                onCreateTask={handleCreateTask}
                onEditTask={handleEditTask}
                onTasksChange={() => {
                  fetchTasks(true);
                  fetchProject();
                }}
                milestones={milestones}
                onCreateMilestone={() => { setEditingMilestone(null); setMilestoneFormOpen(true); }}
                onEditMilestone={(m) => { setEditingMilestone(m); setMilestoneFormOpen(true); }}
              />
            )}
            {currentTab === 3 && (
              <SprintBoard projectId={id} />
            )}
            {currentTab === 4 && (
              <Paper sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Membres de l'équipe ({project?.members?.length || 0})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setShareDialogOpen(true)}
                  >
                    Inviter un membre
                  </Button>
                </Box>
                {project?.members && project.members.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {project.members.map((member) => (
                      <Box
                        key={member.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="bold">{member.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {member.email}
                          </Typography>
                        </Box>
                        
                        {editingMember === member.id ? (
                           <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                             <FormControl size="small" sx={{ minWidth: 150 }}>
                               <InputLabel>Rôle d'accès</InputLabel>
                               <Select
                                 value={member.role}
                                 label="Rôle d'accès"
                                 onChange={(e) => handleUpdateMember(member.id, e.target.value, member.functional_role)}
                               >
                                 {ACCESS_LEVELS.map((level) => (
                                   <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                                 ))}
                               </Select>
                             </FormControl>
                             <FormControl size="small" sx={{ minWidth: 150 }}>
                               <InputLabel>Rôle fonctionnel</InputLabel>
                               <Select
                                 value={member.functional_role || 'Autre'}
                                 label="Rôle fonctionnel"
                                 onChange={(e) => handleUpdateMember(member.id, member.role, e.target.value)}
                               >
                                 {TEAM_ROLES.map((role) => (
                                   <MenuItem key={role} value={role}>{role}</MenuItem>
                                 ))}
                               </Select>
                             </FormControl>
                             <Button size="small" onClick={() => setEditingMember(null)}>Fermer</Button>
                           </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                             <Chip 
                               label={ACCESS_LEVELS.find(l => l.value === member.role)?.label || member.role} 
                               size="small" 
                               color={member.role === 'owner' ? 'secondary' : 'primary'} 
                               variant="outlined" 
                             />
                             <Chip label={member.functional_role || 'Aucun rôle'} size="small" />
                             {(project.user_role === 'owner' || project.user_role === 'admin') && (
                               <Button size="small" onClick={() => setEditingMember(member.id)}>Modifier</Button>
                             )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Aucun membre pour le moment
                  </Typography>
                )}
              </Paper>
            )}
            {currentTab === 5 && (
              <ErrorBoundary key="time">
                <TimeTracker
                  projectId={parseInt(id)}
                  currentUserId={user?.id}
                />
              </ErrorBoundary>
            )}
            {currentTab === 6 && (
              <ErrorBoundary key="automation">
                <Paper sx={{ p: 3 }}>
                  <AutomationBuilder
                    projectId={parseInt(id)}
                    members={project?.members || []}
                  />
                </Paper>
              </ErrorBoundary>
            )}
            {currentTab === 7 && (
              <ErrorBoundary key="workload">
                <Paper sx={{ p: 3 }}>
                  <WorkloadView
                    projectId={parseInt(id)}
                    currentUserId={user?.id}
                  />
                </Paper>
              </ErrorBoundary>
            )}
            {currentTab === 8 && (
              <ErrorBoundary key="budget">
                <BudgetView projectId={parseInt(id)} />
              </ErrorBoundary>
            )}
            {currentTab === 9 && (
              <Paper sx={{ p: 4 }}>
                <AttachmentList relatedType="project" relatedId={id} />
              </Paper>
            )}
            {currentTab === 10 && (
              <Paper sx={{ height: '600px' }}>
                <ProjectChat
                  projectId={id}
                  currentUserId={user?.id}
                />
              </Paper>
            )}
            {currentTab === 11 && (
              <ProjectAIAnalysis
                analysis={aiAnalysis}
                loading={analyzing}
                onRefresh={handleAnalyzeProject}
              />
            )}
            {currentTab === 12 && project?.user_role === 'owner' && (
              <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
                <Typography variant="h6" gutterBottom>Paramètres du projet</Typography>
                <Box component="form" sx={{ mt: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Titre du projet"
                        value={settingsForm.title}
                        onChange={(e) => setSettingsForm({ ...settingsForm, title: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Description"
                        value={settingsForm.description}
                        onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Domaine</InputLabel>
                        <Select
                          value={settingsForm.domain}
                          label="Domaine"
                          onChange={(e) => setSettingsForm({ ...settingsForm, domain: e.target.value })}
                        >
                          {Object.entries(PROJECT_DOMAINS).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Statut</InputLabel>
                        <Select
                          value={settingsForm.status}
                          label="Statut"
                          onChange={(e) => setSettingsForm({ ...settingsForm, status: e.target.value })}
                        >
                          <MenuItem value="active">Actif</MenuItem>
                          <MenuItem value="completed">Terminé</MenuItem>
                          <MenuItem value="on_hold">En pause</MenuItem>
                          <MenuItem value="archived">Archivé</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button 
                        variant="contained" 
                        onClick={handleUpdateProjectSettings}
                      >
                        Enregistrer les modifications
                      </Button>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  <Typography variant="h6" color="error" gutterBottom>Zone de danger</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    La suppression du projet est irréversible et entraînera la perte de toutes les tâches et données associées.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={() => setDeleteProjectDialogOpen(true)}
                  >
                    Supprimer le projet
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </Container>

      {/* Formulaire de tâche */}
      <TaskForm
        open={taskFormOpen}
        onClose={() => {
          setTaskFormOpen(false);
          setEditingTask(null);
        }}
        onSubmit={handleSubmitTask}
        task={editingTask}
        isEdit={isEditTask}
        projectMembers={project?.members || []}
        projectPhases={phases}
        onSubtaskChange={handleSubtaskChange}
        projectCustomFields={customFields}
      />

      {/* Dialogue de suppression de tâche */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer la tâche ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer la tâche "{taskToDelete?.title}" ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={confirmDeleteTask} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de suppression de projet */}
      <Dialog open={deleteProjectDialogOpen} onClose={() => setDeleteProjectDialogOpen(false)}>
        <DialogTitle>Supprimer le projet ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer définitivement le projet "{project?.title}" ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProjectDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleDeleteProject} color="error" variant="contained">
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>

      <ShareProjectDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        project={project}
        onUpdate={fetchProject}
        initialTab={shareDialogTab}
      />

      <MilestoneForm
        open={milestoneFormOpen}
        onClose={() => { setMilestoneFormOpen(false); setEditingMilestone(null); }}
        projectId={id}
        milestone={editingMilestone}
        onSaved={(m) => {
          setMilestones(prev => {
            const exists = prev.find(x => x.id === m.id);
            return exists ? prev.map(x => x.id === m.id ? m : x) : [...prev, m];
          });
        }}
        onDeleted={(milestoneId) => {
          setMilestones(prev => prev.filter(x => x.id !== milestoneId));
        }}
      />

      {/* Gestionnaire de champs personnalisés */}
      <CustomFieldsManager
        open={customFieldsManagerOpen}
        onClose={() => setCustomFieldsManagerOpen(false)}
        projectId={id}
        userRole={project?.user_role}
        onFieldsChange={fetchCustomFields}
      />
    </Box>
  );
}

export default ProjectDetail;
